import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES, findLanguage, type SupportedLanguage } from '@/lib/i18n/languages';
import { LANGUAGE_PILL_TRIGGER_CLASS } from './languagePillStyles';
import { useLanguageMenu } from './useLanguageMenu';

/*
 * LanguagePill — DESIGN_SYSTEM.md §2.4
 *
 * Chrome-family locale switcher. Renders as a pill button (flag + short
 * code + chevron) that opens a dropdown of the supported locales. Pass
 * `availableLanguages` (e.g. `organization.public_locales`) to restrict
 * the picker to a subset — when only one locale is available, the
 * component renders nothing.
 *
 * Persists the choice to `localStorage` under `cleancal_language`.
 */

// The dropdown is loaded on demand (see useLanguageMenu); a visually identical static button
// stands in until it arrives, so single-language pages never download it.
interface LanguagePillProps {
  availableLanguages?: string[];
  /** The full list to offer (default SUPPORTED_LANGUAGES); the sign-up page passes MARKETING_LANGUAGES. */
  languageList?: SupportedLanguage[];
}

export function LanguagePill({ availableLanguages, languageList = SUPPORTED_LANGUAGES }: LanguagePillProps) {
  const { i18n } = useTranslation('common');

  const languages = availableLanguages
    ? languageList.filter((lang) => availableLanguages.includes(lang.code))
    : languageList;

  useEffect(() => {
    if (availableLanguages && availableLanguages.length > 0) {
      const currentLangAvailable = availableLanguages.includes(i18n.language);
      if (!currentLangAvailable) {
        i18n.changeLanguage(availableLanguages[0]);
      }
    }
  }, [availableLanguages, i18n]);

  const hasPicker = languages.length > 1;
  const { Menu, clickedEarly, placeholderProps } = useLanguageMenu(hasPicker);

  if (!hasPicker) {
    return null;
  }

  const currentLanguage = findLanguage(languages, i18n.language);

  const shortCode = currentLanguage.code.split('-')[0].toUpperCase();

  if (Menu) {
    return <Menu languages={languages} currentLanguage={currentLanguage} shortCode={shortCode} defaultOpen={clickedEarly} />;
  }

  return (
    <button
      type="button"
      className={LANGUAGE_PILL_TRIGGER_CLASS}
      {...placeholderProps}
    >
      <span className="text-sm leading-none">{currentLanguage.flag}</span>
      <span className="hidden sm:inline tabular-nums">{shortCode}</span>
      <span className="sr-only">Select language</span>
      <ChevronDown className="h-3 w-3 opacity-60" />
    </button>
  );
}
